import http from './http.service'

// ---- What's New: the changelog panel and the deployment version gate ----

async function getChangelog() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + '/changelog?audience=admin'
    )
    return response.data
}

async function getAllChangelog() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + '/admin/changelog'
    )
    return response.data
}

async function createChangelog(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + '/admin/changelog',
        payload
    )
    return response.data
}

async function updateChangelog(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.put(
        process.env.REACT_APP_API_URL + `/admin/changelog/${payload.id}`,
        payload.data
    )
    return response.data
}

async function deleteChangelog(id) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.delete(
        process.env.REACT_APP_API_URL + `/admin/changelog/${id}`
    )
    return response.data
}

async function getAppVersion() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + '/admin/app-version'
    )
    return response.data
}

async function setAppVersion(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + '/admin/app-version',
        payload
    )
    return response.data
}

async function getProductList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/product?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    // The inventory tabs. `prepare_go_live` and `go_live` are the same words the
    // consignment screens use; reserved / sold / inactive are the car's own.
    if (payload.status) {
        url += `&status=${payload.status}`
    }

    if (payload.hasOwnProperty('category') && Array.isArray(payload.category)) {
        payload.category.forEach((cat) => {
            url += `&category[]=${cat}`
        })
    }

    // Car, Car Plate, Price and Create Date. Anything else the backend ignores and
    // falls back to its own priority-then-newest order.
    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

/**
 * Pin a car to the homepage's Exclusive Deals strip, or take it off.
 *
 * The cap of five is the API's to enforce - a sixth comes back 422 with the reason -
 * so nothing here counts pins or disables the icon on its own.
 */
/** File the workshop's repair quotation against the car. Multipart - it is a file. */
async function uploadRepairQuotation(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/workshop/inspection/${payload.id}/repair-quotation`,
        payload.data
    )
    return response.data
}

// A defect photo for a "No" checkpoint. Uploaded on the spot; the response carries
// the media's url + id, which the checkpoint row keeps.
async function uploadCheckpointPhoto(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/workshop/inspection/${payload.id}/checkpoint-photo`,
        payload.data
    )
    return response.data
}

async function pinProduct(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/product/${payload.id}/pin`,
        { pinned: payload.pinned }
    )
    return response.data
}

async function getProductStatusCount() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/product/status-count`
    )
    return response.data
}

async function createProduct(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/product`,
        payload.data
    )
    return response.data
}

async function getProductInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/product/${payload.id}`,
        payload
    )
    return response.data
}

async function updateProduct(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/product/${payload.id}`,
        payload.data
    )
    return response.data
}

async function toggleProduct(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.patch(
        process.env.REACT_APP_API_URL + `/admin/product/${payload.id}/toggle`,
        payload
    )
    return response.data
}

async function publishProduct(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/product/${payload.id}/publish`,
        payload
    )
    return response.data
}

async function getInspectionList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/workshop/inspections?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.hasOwnProperty('status')) {
        url += `&status=${payload.status}`
    }

    // Consign ID, the one sortable header on this listing.
    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

async function getInspectionTabCount() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/workshop/inspections/tab-count`
    )
    return response.data
}

async function getInspectionInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/workshop/inspection/${payload.id}`
    )
    return response.data
}

async function createInspectionOrder(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/consignment/${payload.consignment_id}/inspection-order`,
        payload
    )
    return response.data
}

/**
 * Start Inspection, and Complete.
 *
 * The status posted back is the one the record's own `next_action` handed out, so the
 * portal never decides which stage follows which - the API owns the sequence, and
 * completing a job is also what hands the car to the office.
 */
async function updateInspectionStatus(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/workshop/inspection/${payload.id}/status`,
        { status: payload.status }
    )
    return response.data
}

async function updateInspection(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/workshop/inspection/${payload.id}`,
        payload.data
    )
    return response.data
}

// The /admin prefix carries the staff guard on the route itself, so a non-staff token
// gets 401 instead of quietly falling through to a list scoped to that person's own
// consignments - which reads as "the data is gone" rather than "you are not allowed".
async function getConsignList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/admin/consignment?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.status) {
        url += `&status=${payload.status}`
    }

    // Only the two sortable columns ever send these; the backend ignores anything else
    // and falls back to its own order.
    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

// A car the office already has: no seller enquiry, no quotation, no inspection fee.
// It is still a consignment - that is the record a car belongs to - and it starts on
// Prepare Go-live, where a consigned car arrives once its inspection is done.
async function createConsign(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/consignment`,
        payload.data
    )
    return response.data
}

async function getConsignInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/consignment/${payload.id}`
    )
    return response.data
}

// Saving the form. Sections are sent as the form draws them - seller, vehicle,
// specifications, features, pricing, deal, inspection - and the backend fans them out
// across the consignment and its car.
//
// `payload.data` and not `payload`: this posted the whole payload object, id and all,
// so the id was written back as a field on every save.
async function updateConsign(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/consignment/${payload.id}`,
        payload.data
    )
    return response.data
}

// Every stage button on every tab - Follow Up, Send Inspection Quotation, Complete
// Payment, Update Go Live, Customer Cancel. Post the status the record's own
// `next_action` handed back rather than deciding here what comes next.
async function updateConsignStatus(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.patch(
        process.env.REACT_APP_API_URL + `/admin/consignment/${payload.id}/status`,
        { status: payload.status, remark: payload.remark }
    )
    return response.data
}

/**
 * Skip the inspection: a followed-up case goes straight to Prepare Go-live.
 *
 * Its own endpoint rather than a status post, because the ordinary stage transition
 * refuses Prepare Go-live without a paid fee and a filed report - and it should keep
 * refusing it for every case that DID go through inspection.
 */
/** Tick a Next Action to-do line on a consignment, or untick it. */
async function confirmConsignChecklistItem(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/consignment/${payload.id}/checklist`,
        { key: payload.key, confirmed: payload.confirmed }
    )
    return response.data
}

/** The same, on a sales order. */
async function confirmSalesOrderChecklistItem(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/sales-order/${payload.id}/checklist`,
        { key: payload.key, confirmed: payload.confirmed }
    )
    return response.data
}

async function skipConsignInspection(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/consignment/${payload.id}/skip-inspection`
    )
    return response.data
}

/**
 * Find claimable inspections by car plate.
 *
 * Deliberately the one workshop call that looks OUTSIDE this account's own jobs -
 * finding an unclaimed one is the whole point. Everything else under /workshop is
 * narrowed to what this workshop has claimed, by the API.
 */
async function searchClaimableInspections(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL +
            `/workshop/inspections/search?car_plate=${encodeURIComponent(payload.car_plate ?? '')}`
    )
    return response.data
}

/** Take an unclaimed job on. Also starts it, like Start Inspection does. */
async function claimInspection(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/workshop/inspections/${payload.id}/claim`
    )
    return response.data
}

async function getConsignStatusCount() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/consignment/status-count`
    )
    return response.data
}

// The /admin listing, filtered to car sales and to the tab being looked at. It used
// the customer route with a `status` that meant something else entirely.
async function getOrderList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/admin/orders?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}&purpose=reservation`

    if (payload.tab) {
        url += `&tab=${payload.tab}`
    }

    // Only Reservation ID and Paid at ever send these; the backend ignores anything
    // else and falls back to its own order.
    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

// The cars a sale may be opened on from the office - only these can be chosen. The API
// decides which they are, a    nd answers with all of them.
async function getReservableCars() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/sales-order/reservable-cars`
    )
    return response.data
}

// A sale the office opens itself - a buyer on the phone or at the showroom, with no
// reservation made through the customer app. Multipart, because the receipt is a file:
// `product_id`, `buyer_phone`, and optionally `note` and `receipt[]`.
async function createSalesOrder(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/sales-order/offline`,
        payload.data
    )
    return response.data
}

async function getSalesOrderTabCount() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/sales-order/tab-count`
    )
    return response.data
}

async function getSalesOrderInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/sales-order/${payload.id}`
    )
    return response.data
}

async function updateSalesOrder(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/sales-order/${payload.id}`,
        payload.data
    )
    return response.data
}

async function submitBuyerInsurance(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/sales-order/${payload.id}/buyer-insurance`,
        payload.data
    )
    return response.data
}

// The stage buttons. One endpoint each: they are done by different desks, with
// different payloads and different guards.
async function salesOrderAction(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/sales-order/${payload.id}/${payload.action}`,
        payload.data || {}
    )
    return response.data
}

// Money in, with the slip finance rules on.
async function submitOrderPayment(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/sales-order/${payload.id}/payments`,
        payload.data
    )
    return response.data
}

async function requestOrderRefund(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/sales-order/${payload.id}/refunds`,
        payload.data
    )
    return response.data
}

// Fixing one dc admin sent back. The same record, addressed by its id - a
// resubmission is never a second payment or a second refund.
async function amendOrderPayment(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/sales-order/${payload.id}/payments/${payload.recordId}`,
        payload.data
    )
    return response.data
}

async function amendOrderRefund(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL +
            `/admin/sales-order/${payload.id}/refunds/${payload.recordId}`,
        payload.data
    )
    return response.data
}

async function getPendingCount() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + '/admin/admin_notification_count'
    )

    return response.data
}

/**
 * Add a make that is not on the list yet.
 *
 * Consignments are typed while the seller is on the phone, and a car whose make dcar
 * has never sold before used to stop that call dead. Both endpoints refuse a duplicate
 * name themselves, so the worst a second attempt can do is come back as a 422.
 */
async function createBrand(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/brand`,
        payload
    )
    return response.data
}

/** Add a model under a make. `brand_id` is required by the API. */
async function createModel(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/model`,
        payload
    )
    return response.data
}

async function getBrandList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/brand?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.hasOwnProperty('status')) {
        url += `&status=${payload.status}`
    }

    const response = await http.get(url)
    return response.data
}

async function getModelList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/model?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.hasOwnProperty('status')) {
        url += `&status=${payload.status}`
    }

    const response = await http.get(url)
    return response.data
}

// Sign in as another user, to see exactly what they see. The backend decides who may
// do this to whom - superadmin or admin, never onto an equal or higher role.
async function impersonateUser(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/user/${payload.id}/impersonate`
    )
    return response.data
}

// Hand the session back. Not under /admin: by now the session belongs to the borrowed
// account, which is usually a customer.
async function stopImpersonating() {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/impersonate/leave`
    )
    return response.data
}

async function getUserList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/admin/user?type=${payload.type}&length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    // Register Date, Username, Name. Anything else falls back to newest first.
    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url, payload)
    return response.data
}

async function getShowroomList(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL +
            `/show_room?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`,
        payload
    )
    return response.data
}

async function createShowroom(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/show_room`,
        payload.data
    )
    return response.data
}

async function updateShowroom(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/show_room/${payload.id}`,
        payload.data
    )
    return response.data
}

async function deleteShowroom(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.delete(
        process.env.REACT_APP_API_URL + `/admin/show_room/${payload.id}`,
        payload.data
    )
    return response.data
}

async function createUser(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/user`,
        payload
    )
    return response.data
}

async function getUserInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/get_profile?id=${payload.id}`,
        payload
    )
    return response.data
}

async function updateUser(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.put(
        process.env.REACT_APP_API_URL + `/admin/user/${payload.id}`,
        payload
    )
    return response.data
}

async function toggleUser(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.patch(
        process.env.REACT_APP_API_URL + `/admin/user/${payload.id}/toggle`,
        payload
    )
    return response.data
}

/**
 * WITHDRAWALS. Money leaving the business, so these are superadmin/admin only - the
 * API refuses everyone else, and the menu is hidden to match.
 */
async function getWithdrawalList(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/admin/withdrawals?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.status) {
        url += `&status=${payload.status}`
    }

    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

async function getWithdrawalInfo(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        `${process.env.REACT_APP_API_URL}/admin/withdrawals/${payload.id}`
    )
    return response.data
}

// Approving takes the receipt as a file, so this one is multipart.
async function approveWithdrawal(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        `${process.env.REACT_APP_API_URL}/admin/withdrawals/${payload.id}/approve`,
        payload.data
    )
    return response.data
}

async function rejectWithdrawal(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        `${process.env.REACT_APP_API_URL}/admin/withdrawals/${payload.id}/reject`,
        payload.data
    )
    return response.data
}

/** Every referral reward the business has paid. Read-only. */
async function getReferralEarnings(payload) {
    http.setToken(localStorage.getItem('token'))
    let url = `${process.env.REACT_APP_API_URL}/admin/referral-earnings?length=${payload.length}&start=${payload.start}&search=${encodeURIComponent(payload.search ?? '')}`

    if (payload.sort) {
        url += `&sort=${payload.sort}&order=${payload.order || 'desc'}`
    }

    const response = await http.get(url)
    return response.data
}

async function createRole(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        `${process.env.REACT_APP_API_URL}/admin/roles`,
        payload.data
    )
    return response.data
}

async function updateRole(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.patch(
        `${process.env.REACT_APP_API_URL}/admin/roles/${payload.id}`,
        payload.data
    )
    return response.data
}

async function deleteRole(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.delete(
        `${process.env.REACT_APP_API_URL}/admin/roles/${payload.id}`
    )
    return response.data
}

/** Delete a staff account (soft). Superadmin only; the API enforces it. */
async function deleteUser(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.delete(
        `${process.env.REACT_APP_API_URL}/admin/user/${payload.id}`
    )
    return response.data
}

/** Reset somebody's password. Superadmin and admin only; the API enforces it. */
async function resetUserPassword(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        `${process.env.REACT_APP_API_URL}/admin/admin_change_password`,
        payload.data
    )
    return response.data
}

async function getRoleList(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.get(
        process.env.REACT_APP_API_URL + `/admin/roles`,
        payload
    )
    return response.data
}

async function updatePassword(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/admin/change_password`,
        payload
    )
    return response.data
}

async function deleteMedia(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.delete(
        process.env.REACT_APP_API_URL + `/media/${payload.id}`
    )
    return response.data
}

// Elect one of a car's exterior photos as its hero - the picture the storefront card,
// the workshop's plate search and the saved-car tile all draw. One car, one hero; the
// API clears the siblings, so the caller only ever names the winner.
// The order a car's photos are shown in, one collection at a time.
async function reorderMedia(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/media/reorder`,
        payload
    )
    return response.data
}

async function setHeroImage(payload) {
    http.setToken(localStorage.getItem('token'))
    const response = await http.post(
        process.env.REACT_APP_API_URL + `/media/${payload.id}/hero`
    )
    return response.data
}

const apiService = {
    getChangelog,
    getAllChangelog,
    createChangelog,
    updateChangelog,
    deleteChangelog,
    getAppVersion,
    setAppVersion,
    getProductList,
    pinProduct,
    uploadRepairQuotation,
    uploadCheckpointPhoto,
    getProductStatusCount,
    getProductInfo,
    createProduct,
    updateProduct,
    toggleProduct,
    publishProduct,

    getInspectionList,
    getInspectionTabCount,
    getInspectionInfo,
    createInspectionOrder,
    updateInspection,
    updateInspectionStatus,

    getConsignList,
    getConsignInfo,
    createConsign,
    updateConsign,
    updateConsignStatus,
    skipConsignInspection,
    confirmConsignChecklistItem,
    confirmSalesOrderChecklistItem,
    searchClaimableInspections,
    claimInspection,
    getConsignStatusCount,

    getOrderList,
    getReservableCars,
    createSalesOrder,
    getSalesOrderTabCount,
    getSalesOrderInfo,
    updateSalesOrder,
    salesOrderAction,
    submitBuyerInsurance,
    submitOrderPayment,
    requestOrderRefund,
    amendOrderPayment,
    amendOrderRefund,

    getBrandList,
    getModelList,
    createBrand,
    createModel,

    getUserList,
    getUserInfo,
    impersonateUser,
    stopImpersonating,
    updateUser,
    createUser,
    toggleUser,

    getShowroomList,
    createShowroom,
    updateShowroom,
    deleteShowroom,
    // getUserInfo,
    // updateUser,
    // createUser,
    // toggleUser,

    getRoleList,
    createRole,
    updateRole,
    deleteRole,
    deleteUser,
    resetUserPassword,
    getWithdrawalList,
    getWithdrawalInfo,
    approveWithdrawal,
    rejectWithdrawal,
    getReferralEarnings,
    updatePassword,
    deleteMedia,
    setHeroImage,
    reorderMedia,

    getPendingCount,
}

export default apiService
